import { vehicleDisplay } from '../../models/shared/customerVehiclesV1Model';
import { MiscSelect } from "../../../../features/dropdowns/views/MiscOptions";
import { useSessionMessageApi } from '../../../../utils/actionableNotifications';
import { CustomerReference } from '../queries/CustomerReference';
import { useQuoteShare } from "../../viewmodels/price-drawer/useQuoteShare";
import { customerTypeLook } from "../../customerTypeLook";
import { injectPartsTemplate as formatPartsTemplate } from "../../models/price-drawer/quoteShareModel";
import { useQuoteApprovalFlow } from "../../viewmodels/price-drawer/useQuoteApprovalFlow";
import { useQuoteEditing, EMPTY_FORM } from "../../viewmodels/price-drawer/useQuoteEditing";
import { usePriceUpdateData } from "../../viewmodels/price-drawer/usePriceUpdateData";
import { recommendedPricing, selectedPartQuote, availablePartQuotes } from "../../models/price-drawer/quoteEditingPolicy";
import { money, baseFromGp, gpFromBase, directPricingAllowed } from "../../models/price-drawer/quotePricingPolicy";
import { useAccess } from "../../../../features/access/context/AccessProvider";
import { legacyVehicleModelId } from "../../models/shared/customerVehiclesV1Model";
import React, { useState, useMemo } from "react";
import { BusinessSendRecovery } from "../modals-misc/BusinessSendRecovery";
import { Drawer, InputNumber, Button, Tag, Spin, Empty, Tooltip, Popconfirm, Modal, Input, Checkbox, Tabs, Alert, Avatar, Collapse, theme } from "antd";
import { Select } from "../../../shared/OptionWidthControl";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, CheckOutlined, CloseOutlined,
  CheckCircleFilled, ShareAltOutlined, SendOutlined, CalculatorOutlined,
  EyeOutlined, UndoOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import AppTheme from "../../../theme/AppTheme";
import { substituteQRText, conditionLabel, sideLabel, useQv4Theme, useQv4ThemeState } from "../../helpers";
import { UnavailablePartsModal } from "../modals-misc/UnavailablePartsModal";
import { usePartHistoryQuotes } from "../../viewmodels/shared/usePartHistoryQuotes";
import { PartHistoryList } from "../shared/PartHistoryList";
import RevisionReason from '../../../payments-v2/views/shared/RevisionReason';
import { paymentErrorMessage } from '../../../payments-v2/utilities/paymentErrors';
import { usePaymentsV2Query } from '../../../payments-v2/viewmodels/usePaymentsV2Query';

dayjs.extend(utc);

// ── Pricing helpers ───────────────────────────────────────────────────────────

// GP% = (SP - CP) / CP × 100  →  SP = CP × (1 + GP/100)
export const spFromGp = baseFromGp;
export const gpFromSp = gpFromBase;

// ── Constants ─────────────────────────────────────────────────────────────────


const TD_STYLE = { padding: "8px 4px", verticalAlign: "middle" };
const TH_STYLE = { fontSize: 10, color: "#aaa", fontWeight: 700, letterSpacing: "0.04em", padding: "6px 4px" };

// ── Main component ────────────────────────────────────────────────────────────

export function PriceUpdateDrawer({ card, open, onClose, userId, onPartsLoaded, authState, approvalDecidedTick, userMap }) {
  const antMessage = useSessionMessageApi();
  const { t, isDark } = useQv4Theme();


  // quotePricing: { [quoteId]: { sp, gp, _cp } }
  // _cp is stored to detect when quoted_price was changed on edit



  const [shareOpen, setShareOpen] = useState(false);
  const [unavailableNoticeParts, setUnavailableNoticeParts] = useState(null);
  const { can } = useAccess();
  const payments = usePaymentsV2Query(card?.id, { enabled: open && can('payments.view') });
  const lockedInvoice = payments.projection?.invoice_lock_state === 'locked';

  const { approvalHistory, setApprovalHistory, withdrawing, pendingApproval, resubmitModalOpen, setResubmitModalOpen, resubmitReason, setResubmitReason, refreshApprovalHistory, handleWithdrawApproval } = useQuoteApprovalFlow({
    card, open, userId, approvalDecidedTick, notify: antMessage,
  });

  const { parts, setParts, taskLoading, costPrices, costPricesLoading, quotePricing, setQuotePricing, pricingTimeline, taskCustomer, taskCustomerType, setPricingReload, supplierOptions } = usePriceUpdateData({
    card, open, onPartsLoaded, setApprovalHistory, resetEditing: () => resetEditing(), notify: antMessage,
  });

  const { addingPartId, setAddingPartId, addForm, setAddForm, addSaving, editingQuoteId, setEditingQuoteId, editForm, setEditForm, editSaving, selectingPartId, pricingSaves, overrideConfirm, setOverrideConfirm, overrideNote, setOverrideNote, withOverrideGuard, handleAddQuote, handleEditQuote, onDeleteQuote_original, handleSelectQuote, handleCalculateAll, handleRoundAll, resetEditing } = useQuoteEditing({
    card, userId, parts, setParts, costPrices, setQuotePricing, notify: antMessage,
  });

  const [previewOpen, setPreviewOpen] = useState(false);

  // ── Handlers ────────────────────────────────────────────────────────────────

  // Count quotes not yet priced (to drive the button label)
  const unpricedCount = useMemo(() =>
    parts.reduce((n, p) =>
      n + (p.supplier_quotes ?? []).filter(q => !quotePricing[q.id]).length, 0),
    [parts, quotePricing]
  );

  // Supplier price history — one request for every part in this task, fed
  // down to each PartSection as its own slice. card.vehicle is shaped
  // inconsistently depending on how this drawer was opened: WorkflowTaskDrawer
  // names the raw make/model FK ids `brand`/`model`, the board's own
  // priceCard path names the same data `brand_id`/`model_id` — read both.
  // See docs/superpowers/specs/2026-08-22-supplier-price-history-design.md.
  const { resultsByKey: partHistoryByKey } = usePartHistoryQuotes({
    makeId: card?.vehicle?.brand_id ?? card?.vehicle?.brand ?? null,
    modelId: legacyVehicleModelId(card?.vehicle),
    year: card?.vehicle?.year ?? null,
    countrySpec: card?.vehicle?.country_spec ?? null,
    excludeQueryPk: card?.id ?? null,
    parts: parts.map(p => ({ key: String(p.id), canonicalPartId: p.uzdpart360_part_id ?? p.canonical_part?.id ?? null, partId: p.part_id ?? null, partName: p.part_name })),
  });

  const handleClose = () => {
    setAddingPartId(null);
    setAddForm(EMPTY_FORM);
    setEditingQuoteId(null);
    setEditForm(EMPTY_FORM);
    onClose();
  };

  const v = card?.vehicle;
  const vehicleLabel = v
    ? vehicleDisplay(v)
    : null;
  const customerPhone = card?.customer?.customer_mobile || card?.customer_mobile || null;

  return (
    <Drawer
      title={
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>
              Price Update{card?.query_id ? ` — #${card.query_id}` : ""} <CustomerReference value={card?.customer_reference} />
            </div>
            {vehicleLabel && (
              <div style={{ fontSize: 12, color: t.textSub, fontWeight: 400, marginTop: 2 }}>{vehicleLabel}</div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {unpricedCount > 0 && (
              <Tooltip title={`Fill GP% and selling price for ${unpricedCount} unpriced quote${unpricedCount !== 1 ? "s" : ""} using cost tier lookup`}>
                <Button
                  icon={<CalculatorOutlined />}
                  size="small"
                  loading={costPricesLoading}
                  disabled={costPricesLoading}
                  onClick={handleCalculateAll}
                  style={{ flexShrink: 0 }}
                >
                  Calculate prices ({unpricedCount})
                </Button>
              </Tooltip>
            )}
            {Object.keys(quotePricing).length > 0 && (
              <Tooltip title="Round all selling prices to nearest whole number">
                <Button
                  icon={<span style={{ fontWeight: 700, fontSize: 11 }}>.0</span>}
                  size="small"
                  onClick={handleRoundAll}
                  style={{ flexShrink: 0 }}
                >
                  Round prices
                </Button>
              </Tooltip>
            )}
            {customerPhone && can("sales_pipeline.share_customer_quote") && (
              <Tooltip title={pendingApproval ? "A quote is already awaiting admin approval — withdraw it first to submit a new one" : (can("sales_pipeline.send_quote_without_approval") ? "Configured for direct quote sharing; preflight confirms execution" : "Quote sharing requires approval")}>
                <Button
                  icon={<ShareAltOutlined />}
                  size="small"
                  disabled={!!pendingApproval || costPricesLoading || taskLoading || pricingSaves > 0}
                  loading={pricingSaves > 0}
                  onClick={() => withOverrideGuard(
                    "Share quote after payment?",
                    "This query already has a payment in progress or completed. Sharing a new quote will submit it through the normal customer-approval flow again.",
                    () => {
                      const hasApproved = approvalHistory.some(a => a.status === "approved");
                      if (hasApproved) { setResubmitReason(""); setResubmitModalOpen(true); }
                      else setShareOpen(true);
                    }
                  )}
                  style={{ flexShrink: 0 }}
                >
                  {lockedInvoice ? 'Share revised quote' : 'Share quote'}
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
      }
      open={open}
      onClose={handleClose}
      width={780}
      destroyOnHidden
      styles={{ body: { padding: 16, overflowY: "auto" } }}
    >
      {approvalHistory.length > 0 && (() => {
        const latest = approvalHistory[0];
        const isPending = latest.status === "pending";
        const isRejected = latest.status === "rejected";
        const deciderName = latest.decided_by ? (userMap?.[latest.decided_by] ?? `User #${latest.decided_by}`) : null;
        const older = approvalHistory.slice(1);
        return (
          <div style={{ marginBottom: 12 }}>
            <Alert
              type={isPending ? "warning" : isRejected ? "error" : "success"}
              showIcon
              message={
                isPending ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                    <div style={{ fontSize: 12 }}>
                      Quote pending admin approval since{" "}
                      <strong>{dayjs.utc(latest.submitted_at.replace(" ", "T")).format("DD/MM/YYYY HH:mm")}</strong>
                      {" "}— you cannot submit another until this is decided or withdrawn.
                      {latest.resubmission_reason && latest.resubmission_reason !== "null" && (
                        <div style={{ marginTop: 4, color: "#d48806" }}>
                          Resubmit reason: {latest.resubmission_reason}
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <Button size="small" icon={<EyeOutlined />} onClick={() => setPreviewOpen(true)}>View quote</Button>
                      <Popconfirm
                        title="Withdraw approval request?"
                        description="This cannot be undone. You can re-submit after withdrawing."
                        onConfirm={handleWithdrawApproval}
                        okText="Withdraw"
                        okButtonProps={{ danger: true }}
                        cancelText="Keep"
                      >
                        <Button size="small" danger loading={withdrawing}>Withdraw</Button>
                      </Popconfirm>
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: 12 }}>
                    <strong>{isRejected ? "Quote rejected" : "Quote approved"}</strong>
                    {deciderName && <> by <strong>{deciderName}</strong></>}
                    {isRejected && latest.decision_reason && <> — {latest.decision_reason}</>}
                    {isRejected && <> · You can now update and re-submit for approval.</>}
                    {latest.resubmission_reason && latest.resubmission_reason !== "null" && (
                      <div style={{ marginTop: 4, color: "#d48806" }}>
                        Resubmit reason: {latest.resubmission_reason}
                      </div>
                    )}
                  </div>
                )
              }
            />
            {older.length > 0 && (
              <Collapse
                ghost
                size="small"
                style={{ marginTop: 2 }}
                items={[{
                  key: "hist",
                  label: <span style={{ fontSize: 11, color: "#8c8c8c" }}>Previous approvals ({older.length})</span>,
                  children: (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {older.map(a => {
                        const dName = a.decided_by ? (userMap?.[a.decided_by] ?? `User #${a.decided_by}`) : null;
                        const aRejected = a.status === "rejected";
                        const aApproved = a.status === "approved";
                        return (
                          <div key={a.id} style={{
                            fontSize: 11, padding: "6px 10px", borderRadius: 4,
                            background: aRejected
                              ? (isDark ? "rgba(255,77,79,0.12)" : "#fff2f0")
                              : aApproved
                                ? (isDark ? "rgba(69,153,34,0.12)" : "#f6ffed")
                                : (isDark ? "rgba(250,173,20,0.12)" : "#fffbe6"),
                            border: `1px solid ${aRejected ? "#ffccc7" : aApproved ? "#b7eb8f" : "#ffe58f"}`,
                          }}>
                            <span style={{ fontWeight: 600, color: aRejected ? "#cf1322" : aApproved ? "#389e0d" : "#d48806" }}>
                              {aRejected ? "Rejected" : aApproved ? "Approved" : "Pending"}
                            </span>
                            {dName && <span style={{ color: "#595959" }}> by {dName}</span>}
                            {a.decided_at && (
                              <span style={{ color: "#8c8c8c" }}>
                                {" "}· {dayjs.utc(a.decided_at.replace(" ", "T")).format("DD MMM · HH:mm")}
                              </span>
                            )}
                            {a.resubmission_reason && a.resubmission_reason !== "null" && (
                              <div style={{ color: "#d48806", marginTop: 2 }}>Resubmit reason: {a.resubmission_reason}</div>
                            )}
                            {a.decision_reason && (
                              <div style={{ color: "#8c8c8c", marginTop: 2 }}>Decision: {a.decision_reason}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ),
                }]}
              />
            )}
          </div>
        );
      })()}
      {taskLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 48 }}><Spin /></div>
      ) : parts.length === 0 ? (
        <Empty description="No parts found" style={{ marginTop: 48 }} />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {parts.map(part => (
            <PartSection
              key={part.id}
              part={part}
              supplierOptions={supplierOptions}
              quotePricing={quotePricing}
              costPrices={costPrices}
              addingPartId={addingPartId}
              setAddingPartId={setAddingPartId}
              addForm={addForm}
              setAddForm={setAddForm}
              addSaving={addSaving}
              onAddQuote={(partId) => withOverrideGuard(
                "Add quote after payment?",
                "This query already has a payment in progress or completed. Adding a new quote here is a record-keeping override.",
                (note) => handleAddQuote(partId, note)
              )}
              editingQuoteId={editingQuoteId}
              setEditingQuoteId={setEditingQuoteId}
              editForm={editForm}
              setEditForm={setEditForm}
              editSaving={editSaving}
              onEditSave={(quoteId, partId) => withOverrideGuard(
                "Edit quote after payment?",
                "This query already has a payment in progress or completed. Editing this quote is a record-keeping override.",
                (note) => handleEditQuote(quoteId, partId, note)
              )}
              onDeleteQuote={(quoteId, partId) => withOverrideGuard(
                "Delete quote after payment?",
                "This query already has a payment in progress or completed.",
                () => onDeleteQuote_original(quoteId, partId)
              )}
              onSelectQuote={(partId, quoteId) => withOverrideGuard(
                "Change selected quote after payment?",
                "This query already has a payment in progress or completed. This changes which quote is used for pricing.",
                () => handleSelectQuote(partId, quoteId)
              )}
              selectingPartId={selectingPartId}
              history={partHistoryByKey[String(part.id)] || []}
              onUseHistoryPrice={(match) => {
                setAddingPartId(part.id);
                setAddForm({
                  supplier_id: supplierOptions.some(s => s.value === match.supplier_id) ? match.supplier_id : null,
                  price: match.quoted_price,
                  warranty_days: match.warranty_days ?? 0,
                  delivery_charge: 0,
                  gp: null,
                  sp: null,
                });
              }}
            />
          ))}

        </div>
      )}

      <Modal
        open={resubmitModalOpen}
        title="Reason for new quote"
        okText="Continue"
        okButtonProps={{ disabled: !resubmitReason.trim() }}
        onOk={() => { setResubmitModalOpen(false); setShareOpen(true); }}
        onCancel={() => setResubmitModalOpen(false)}
        width={420}
      >
        <p style={{ fontSize: 13, marginBottom: 8 }}>
          A quote for this query was already approved. Please explain why you are submitting a new one — the admin will see this reason.
        </p>
        <Input.TextArea
          autoFocus
          rows={3}
          maxLength={500}
          showCount
          placeholder="e.g. Wrong part was quoted, customer requested a different option..."
          value={resubmitReason}
          onChange={e => setResubmitReason(e.target.value)}
        />
      </Modal>

      <ShareQuoteModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        card={card}
        taskCustomer={taskCustomer}
        taskCustomerType={taskCustomerType}
        parts={parts}
        quotePricing={quotePricing}
        costPrices={costPrices} pricingTimeline={pricingTimeline}
        onStalePricing={() => { setShareOpen(false); setPricingReload(v => v + 1); }}
        authState={authState}
        userId={userId}
        customerPhone={customerPhone}
        resubmissionReason={resubmitReason || null}
        onApprovalCreated={refreshApprovalHistory}
        onSuggestUnavailableNotice={setUnavailableNoticeParts}
      />

      <ShareQuoteModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        card={card}
        parts={parts}
        quotePricing={quotePricing}
        costPrices={costPrices} pricingTimeline={pricingTimeline}
        onStalePricing={() => { setShareOpen(false); setPricingReload(v => v + 1); }}
        authState={authState}
        userId={userId}
        customerPhone={customerPhone}
        readOnly
        preloadedPayload={pendingApproval?.payload}
      />

      <UnavailablePartsModal
        open={!!unavailableNoticeParts}
        queryPk={card?.id}
        queryId={card?.query_id}
        customerName={taskCustomer?.customer_name || card?.customer?.customer_name}
        customerPhone={customerPhone}
        unavailableParts={unavailableNoticeParts || []}
        userId={userId}
        onClose={() => setUnavailableNoticeParts(null)}
        onSuccess={() => setUnavailableNoticeParts(null)}
      />

      <Modal
        open={!!overrideConfirm}
        title={overrideConfirm?.title}
        okText="Continue"
        okButtonProps={{ danger: true }}
        onOk={() => { const c = overrideConfirm; setOverrideConfirm(null); c?.onConfirm(overrideNote.trim() || null); }}
        onCancel={() => setOverrideConfirm(null)}
        width={440}
      >
        <p style={{ fontSize: 13, marginBottom: 8 }}>{overrideConfirm?.description}</p>
        <Input.TextArea
          rows={2}
          maxLength={300}
          showCount
          placeholder="Optional note (e.g. price matched competitor)"
          value={overrideNote}
          onChange={e => setOverrideNote(e.target.value)}
        />
      </Modal>
    </Drawer>
  );
}

// ── Part section ──────────────────────────────────────────────────────────────

function PartSection({
  part, supplierOptions, quotePricing, costPrices,
  addingPartId, setAddingPartId, addForm, setAddForm, addSaving, onAddQuote,
  editingQuoteId, setEditingQuoteId, editForm, setEditForm, editSaving,
  onEditSave, onDeleteQuote, onSelectQuote, selectingPartId,
  history = [], onUseHistoryPrice,
}) {
  const quotes = availablePartQuotes(part);
  const selectedQuote = selectedPartQuote(part);
  const condition = part.condition_preference_label || conditionLabel(part.condition_preference ?? part.condition ?? part.part_condition);
  const isAdding = addingPartId === part.id;
  const isSelecting = selectingPartId === part.id;
  const { t } = useQv4Theme();

  return (
    <div style={{ border: `1px solid ${t.border}`, borderRadius: 8, overflow: "hidden" }}>
      {/* Part header */}
      <div style={{
        background: t.headerBg, padding: "9px 14px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        borderBottom: `1px solid ${t.border}`,
      }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>{part.part_name}</span>
          <div style={{ fontSize: 11, color: t.textSub, overflowWrap: "anywhere" }}>
            Qty: {part.quantity ?? 1}
            {part.side != null && part.side !== "" ? ` · ${part.side_label || sideLabel(part.side)}` : ""}
            {condition && <> · <span title="Requested part condition">{condition}</span></>}
            {part.part_number ? ` · Part# ${part.part_number}` : ""}
            {(part.old_exchange === true || part.old_exchange === 1 || part.old_exchange === "1") && <Tag color="gold" style={{ marginLeft: 6, fontSize: 10 }}>With Old Exchange</Tag>}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {selectedQuote && (
            <span style={{ fontSize: 11, color: "#389e0d", fontWeight: 600 }}>
              <CheckCircleFilled style={{ marginRight: 4 }} />
              Quote selected
              {part.selling_price != null && (
                <span style={{ marginLeft: 6, fontWeight: 700 }}>
                  · AED {Number(part.selling_price).toLocaleString()}
                </span>
              )}
            </span>
          )}
          <Tag color={quotes.length > 0 ? "blue" : "default"} style={{ margin: 0 }}>
            {quotes.length} quote{quotes.length !== 1 ? "s" : ""}
          </Tag>
        </div>
      </div>

      {/* Quotes table */}
      {(quotes.length > 0 || isAdding) && (
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: 28 }} />
            <col />
            <col style={{ width: 90 }} />
            <col style={{ width: 60 }} />
            <col style={{ width: 84 }} />
            <col style={{ width: 96 }} />
            <col style={{ width: 64 }} />
          </colgroup>
          <thead>
            <tr style={{ background: t.headerBg, borderBottom: `1px solid ${t.border}` }}>
              <th style={{ ...TH_STYLE, paddingLeft: 14 }} />
              <th style={{ ...TH_STYLE, textAlign: "left" }}>SUPPLIER</th>
              <th style={{ ...TH_STYLE, textAlign: "right" }}>COST</th>
              <th style={{ ...TH_STYLE, textAlign: "right" }}>WARRANTY</th>
              <th style={{ ...TH_STYLE, textAlign: "right" }}>GP %</th>
              <th style={{ ...TH_STYLE, textAlign: "right" }}>BASE SELL PRICE</th>
              <th style={{ ...TH_STYLE, paddingRight: 14 }} />
            </tr>
          </thead>
          <tbody>
            {quotes.map(q =>
              editingQuoteId === q.id ? (
                <QuoteFormRow
                  key={q.id}
                  supplierOptions={supplierOptions}
                  costPrices={costPrices}
                  form={editForm} setForm={setEditForm}
                  saving={editSaving} saveDisabled={editForm.price == null}
                  onSave={() => onEditSave(q.id, part.id)}
                  onCancel={() => { setEditingQuoteId(null); setEditForm(EMPTY_FORM); }}
                  bg="#fffbe6"
                  isSelectedQuote={part.selected_quote_id === q.id}
                />
              ) : (
                <QuoteRow
                  key={q.id}
                  quote={q}
                  pricing={quotePricing[q.id] ?? null}
                  isSelected={part.selected_quote_id === q.id}
                  isSelecting={isSelecting}
                  onSelect={() => onSelectQuote(part.id, q.id)}
                  onEdit={() => {
                    setEditingQuoteId(q.id);
                    const cp = Number(q.quoted_price);
                    const existing = quotePricing[q.id];
                    const rec = existing ?? recommendedPricing(costPrices, cp);
                    setEditForm({
                      supplier_id: q.supplier_id,
                      price: cp,
                      warranty_days: q.warranty_days,
                      delivery_charge: Number(q.delivery_cost),
                      gp: rec.gp,
                      sp: rec.sp,
                    });
                  }}
                  onDelete={() => onDeleteQuote(q.id, part.id)}
                />
              )
            )}
            {isAdding && (
              <QuoteFormRow
                supplierOptions={supplierOptions}
                costPrices={costPrices}
                form={addForm} setForm={setAddForm}
                saving={addSaving} saveDisabled={!addForm.supplier_id || addForm.price == null}
                onSave={() => onAddQuote(part.id)}
                onCancel={() => { setAddingPartId(null); setAddForm(EMPTY_FORM); }}
                bg="#f0f9ff"
                isSelectedQuote={false}
              />
            )}
          </tbody>
        </table>
      )}

      {/* Add quote button */}
      {!isAdding && (
        <div style={{ padding: "10px 14px", borderTop: quotes.length > 0 ? `1px solid ${t.border}` : "none" }}>
          <Button size="small" type="dashed" icon={<PlusOutlined />} block
            onClick={() => { setAddingPartId(part.id); setAddForm(EMPTY_FORM); }}
          >
            Add Quote
          </Button>
        </div>
      )}

      {/* Supplier price history — always available, but only takes up
          space by default when there's nothing else here yet (see
          docs/superpowers/specs/2026-08-22-supplier-price-history-design.md).
          Matches under a week old get a one-click "use this price" that
          pre-fills the Add Quote form above; older ones are reference only,
          since the price/availability may have moved on by now. */}
      {history.length > 0 && (
        <Collapse
          ghost
          size="small"
          defaultActiveKey={quotes.length === 0 ? ["history"] : []}
          style={{ borderTop: `1px solid ${t.border}` }}
          items={[{
            key: "history",
            label: (
              <span style={{ fontSize: 12, color: t.textSub }}>
                {history.length} past quote{history.length !== 1 ? "s" : ""} for this part
              </span>
            ),
            children: (
              <PartHistoryList
                matches={history}
                renderAction={match => (
                  match.quoted_at && dayjs().diff(dayjs(match.quoted_at), "day") < 7 ? (
                    <Button size="small" type="link" onClick={() => onUseHistoryPrice?.(match)}>
                      Use this price
                    </Button>
                  ) : null
                )}
              />
            ),
          }]}
        />
      )}
    </div>
  );
}

// ── Quote row (tr) ────────────────────────────────────────────────────────────

function QuoteRow({ quote, pricing, isSelected, isSelecting, onSelect, onEdit, onDelete }) {
  const supplierLabel = quote.supplier_name && quote.supplier_name !== quote.supplier_number
    ? quote.supplier_name : quote.supplier_number;
  const showNumber = quote.supplier_name && quote.supplier_name !== quote.supplier_number;
  const cp = Number(quote.quoted_price || 0);
  const { t, isDark } = useQv4Theme();

  return (
    <tr style={{ background: isSelected ? (isDark ? "rgba(69,153,34,0.12)" : "#f6ffed") : undefined, transition: "background 0.2s" }}>
      <td style={{ ...TD_STYLE, paddingLeft: 14 }}>
        <Tooltip title={isSelected ? "Deselect" : "Select this quote"}>
          <button
            aria-label={`${isSelected ? "Deselect" : "Select"} quote from ${supplierLabel}`}
            onClick={onSelect}
            disabled={isSelecting}
            style={{
              width: 20, height: 20, borderRadius: "50%",
              border: "none", background: "transparent",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: isSelecting ? "default" : "pointer",
              padding: 0, outline: "none", opacity: isSelecting ? 0.5 : 1,
            }}
          >
            {isSelected
              ? <CheckCircleFilled style={{ fontSize: 18, color: "#52c41a" }} />
              : <span style={{ width: 18, height: 18, borderRadius: "50%", border: `1.5px solid ${t.border}`, display: "block" }} />
            }
          </button>
        </Tooltip>
      </td>
      <td style={TD_STYLE}>
        <div style={{ fontWeight: 500, fontSize: 12 }}>{supplierLabel}</div>
        {showNumber && <div style={{ fontSize: 10, color: t.textSub }}>{quote.supplier_number}</div>}
      </td>
      <td style={{ ...TD_STYLE, textAlign: "right", fontWeight: 600, fontSize: 12 }}>{cp.toLocaleString()}</td>
      <td style={{ ...TD_STYLE, textAlign: "right", color: t.textSub, fontSize: 12 }}>
        {quote.warranty_days ? `${quote.warranty_days}d` : "—"}
      </td>

      <td style={{ ...TD_STYLE, textAlign: "right", color: t.textSub, fontSize: 12 }}>
        {pricing?.gp != null ? `${(Math.round(pricing.gp * 100) / 100).toLocaleString()}%` : "—"}
      </td>
      <td style={{ ...TD_STYLE, textAlign: "right", fontWeight: 600, fontSize: 12 }}>
        {pricing?.sp != null ? `AED ${(Math.round(pricing.sp * 100) / 100).toLocaleString()}` : "—"}
      </td>
      <td style={{ ...TD_STYLE, paddingRight: 14 }}>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Tooltip title="Edit">
            <Button aria-label={`Edit quote from ${supplierLabel}`} size="small" type="text" icon={<EditOutlined />} onClick={onEdit} />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm title="Delete this quote?" okText="Yes" cancelText="No" onConfirm={onDelete}>
              <Button size="small" type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </div>
      </td>
    </tr>
  );
}

// ── Quote form row (tr) — used for both add and edit ──────────────────────────

function QuoteFormRow({ supplierOptions, costPrices, form, setForm, onSave, onCancel, saving, saveDisabled, bg, isSelectedQuote }) {
  const priced = form.price != null;
  const { isDark } = useQv4Theme();
  const rowBg = isDark
    ? (bg === "#fffbe6" ? "rgba(250,173,20,0.12)" : bg === "#f0f9ff" ? "rgba(24,144,255,0.12)" : bg)
    : bg;

  // Cost drives a full recompute every keystroke — always overrides whatever
  // GP%/SP held before, since cost is the one field allowed to touch both.
  const onCostChange = (v) => {
    const rec = recommendedPricing(costPrices, v);
    setForm(f => ({ ...f, price: v, gp: rec.gp, sp: rec.sp }));
  };
  // GP%/SP only affect each other — cost is never touched by either.
  const onGpChange = (v) => {
    if (!priced) return;
    setForm(f => ({ ...f, gp: v, sp: v != null ? spFromGp(f.price, v) : f.sp }));
  };
  const onSpChange = (v) => {
    if (!priced) return;
    setForm(f => ({ ...f, sp: v, gp: v != null ? gpFromSp(f.price, v) : f.gp }));
  };
  const onResetPricing = () => {
    if (!priced) return;
    const rec = recommendedPricing(costPrices, form.price);
    setForm(f => ({ ...f, gp: rec.gp, sp: rec.sp }));
  };

  const supplierSelect = (
    <Select
      showSearch placeholder="Supplier"
      value={form.supplier_id}
      options={supplierOptions}
      filterOption={(input, option) => {
        const q = input.toLowerCase();
        return (option.name || "").toLowerCase().includes(q) || (option.number || "").toLowerCase().includes(q);
      }}
      optionRender={opt => (
        <div>
          <div style={{ fontWeight: 500, fontSize: 13 }}>
            {opt.data.name && opt.data.name !== opt.data.number ? opt.data.name : opt.data.number}
          </div>
          {opt.data.name && opt.data.name !== opt.data.number && (
            <div style={{ fontSize: 11, color: "#aaa" }}>{opt.data.number}</div>
          )}
        </div>
      )}
      onChange={v => setForm(f => ({ ...f, supplier_id: v }))}
      size="small" style={{ width: "100%" }}
    />
  );

  return (
      <>
        {!isSelectedQuote && (
          <tr style={{ background: rowBg }}>
            <td colSpan={7} style={{ padding: "6px 14px 2px" }}>
              <div style={{
                display: "flex", alignItems: "center", gap: 6,
                fontSize: 11, color: "#874d00", background: isDark ? "rgba(250,173,20,0.12)" : "#fffbe6",
                border: "1px solid #ffe58f", borderRadius: 4, padding: "4px 8px",
              }}>
                <InfoCircleOutlined style={{ fontSize: 11, flexShrink: 0 }} />
                Preview prices — GP% and Sell Price here aren't saved until this quote is selected as the customer's chosen quote.
              </div>
            </td>
          </tr>
        )}
        <tr data-testid="quote-form-supplier-row" style={{ background: rowBg }}>
          <td colSpan={7} style={{ padding: "8px 14px 4px" }}>
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4, color: "#8c8c8c" }}>SUPPLIER</div>
            {supplierSelect}
          </td>
        </tr>
        <tr data-testid="quote-form-fields-row" style={{ background: rowBg }}>
          <td colSpan={7} style={{ padding: "4px 14px 10px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(120px, 1fr) minmax(95px, .8fr) minmax(120px, 1fr) minmax(140px, 1.1fr) auto", gap: 8, alignItems: "end" }}>
              <div>
                <div style={{ fontSize: 10, marginBottom: 3, color: "#8c8c8c" }}>COST</div>
                <InputNumber placeholder="Price" value={form.price} onChange={onCostChange} min={0} size="small" style={{ width: "100%" }} prefix="AED" controls={false} />
              </div>
              <div>
                <div style={{ fontSize: 10, marginBottom: 3, color: "#8c8c8c" }}>WARRANTY</div>
                <InputNumber placeholder="Days" value={form.warranty_days} onChange={v => setForm(f => ({ ...f, warranty_days: v }))} min={0} size="small" style={{ width: "100%" }} controls={false} />
              </div>

              <div>
                <div style={{ fontSize: 10, marginBottom: 3, color: "#8c8c8c" }}>GP %</div>
                <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <InputNumber placeholder="GP%" value={form.gp != null ? Math.round(form.gp * 100) / 100 : undefined} onChange={onGpChange} disabled={!priced} min={-100} precision={2} size="small" style={{ width: "100%" }} suffix="%" controls={false} />
                  {priced && <Tooltip title="Reset to recommended GP%/price"><Button size="small" type="text" icon={<UndoOutlined />} onClick={onResetPricing} style={{ flexShrink: 0, padding: "0 2px" }} /></Tooltip>}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, marginBottom: 3, color: "#8c8c8c" }}>BASE SELL PRICE</div>
                <InputNumber placeholder="Sell price" value={form.sp != null ? Math.round(form.sp * 100) / 100 : undefined} onChange={onSpChange} disabled={!priced} min={0} precision={2} size="small" style={{ width: "100%" }} prefix="AED" controls={false} />
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                <Button aria-label="Cancel quote changes" size="small" icon={<CloseOutlined />} onClick={onCancel} />
                <Button aria-label="Save quote" size="small" type="primary" icon={<CheckOutlined />} loading={saving} disabled={saveDisabled} onClick={onSave} />
              </div>
            </div>
          </td>
        </tr>
      </>
  );
}

// ── Share Quote Modal ─────────────────────────────────────────────────────────

export function injectPartsTemplate(templateText, card, selectedParts, deliveryPerPart) {
  return formatPartsTemplate(templateText, card, selectedParts, deliveryPerPart, { conditionLabel, sideLabel });
}

function ShareQuoteModal(props) {
  return <AppTheme><ThemedShareQuoteModal {...props} /></AppTheme>;
}

function ThemedShareQuoteModal({ open, onClose, card, taskCustomer, taskCustomerType, parts, quotePricing, costPrices = [], pricingTimeline, onStalePricing, authState, userId, customerPhone, readOnly, preloadedPayload, onApprovalCreated, resubmissionReason, onSuggestUnavailableNotice }) {
  const antMessage = useSessionMessageApi();
  const { can } = useAccess();
  const { t } = useQv4ThemeState();
  const { token } = theme.useToken();

  const { revision, deliveryEnabled, setDeliveryEnabled, activeTab, setActiveTab, actionSnippet, qrLoading, bindingError, includedPartIds, partSp, setPartSp, vatMode, setVatMode, vatPct, setVatPct, partOldExchange, deliveryAmount, setDeliveryAmount, minDeliveryAmount, messageText, setMessageText, textEditEnabled, setTextEditEnabled, textManuallyEdited, setTextManuallyEdited, isB2B, isB2C, typeKnown, pricingNotice, pricingReview, pricedById, selectedPartsForInjection, deliveryPerPart, togglePart, toggleOldExchange, sending, manualShare, setManualShare, recordingManual, handleSend, handleManualShare } = useQuoteShare({
    open, onClose, card, taskCustomer, taskCustomerType, parts, quotePricing, costPrices, pricingTimeline, onStalePricing, authState, userId, customerPhone, readOnly, preloadedPayload, onApprovalCreated, resubmissionReason, onSuggestUnavailableNotice, notify: antMessage,
    confirmUnavailable: zeroQuoteParts => {
          Modal.confirm({
            title: "Some parts still have no quotes",
            content: `${zeroQuoteParts.length} part(s) — ${zeroQuoteParts.map(p => p.part_name).join(", ")} — have no supplier quotes. Send the customer a message about them now? You can also do this later from the task drawer's "Notify Unavailable Parts" button.`,
            okText: "Send now",
            cancelText: "Not now",
            onOk: () => onSuggestUnavailableNotice(zeroQuoteParts),
          });
    },
  });

  const unquotedParts = readOnly ? [] : parts.filter(p => !p.selected_quote_id);
  const customerName = readOnly
    ? (preloadedPayload?.customer_name || "—")
    : (card?.customer?.customer_name || card?.customer_name || "—");
  const queryId = readOnly ? preloadedPayload?.query_id : (card?.query_id || card?.id);

  const leftCol = (
    <div style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 8, paddingRight: 4 }}>
      {readOnly && (
        <Alert
          type="info"
          showIcon
          style={{ fontSize: 11, flexShrink: 0 }}
          message="This is a read-only preview of the quote sent for approval."
        />
      )}
      <div style={{ padding: "8px 10px", background: t.hover, borderRadius: 6, fontSize: 12, flexShrink: 0 }}>
        <div style={{ color: t.textSub, marginBottom: 6 }}><strong style={{ color: t.text }}>Query:</strong> #{queryId} <CustomerReference value={readOnly ? preloadedPayload?.customer_reference : card?.customer_reference} /></div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Avatar
            size={32}
            icon={customerTypeLook(isB2B ? 0 : isB2C ? 1 : null).icon}
            style={{ backgroundColor: customerTypeLook(isB2B ? 0 : isB2C ? 1 : null).color, flexShrink: 0 }}
          />
          <div>
            <div style={{ fontWeight: 600, color: t.text }}>{customerName}</div>
            <div style={{ color: t.textSub, fontSize: 11 }}>
              {isB2B ? "B2B · Garage" : isB2C ? "B2C · Retail" : "Type unknown"}
              {!readOnly && customerPhone ? ` · ${customerPhone}` : ""}
            </div>
          </div>
        </div>
      </div>

      {pricingNotice && <Alert type="warning" showIcon message={pricingNotice} />}
      {!readOnly && revision.locked && <RevisionReason vm={revision} />}
      {!readOnly && revision.error && <Alert type="error" message={paymentErrorMessage(revision.error)} />}
      {!readOnly && <BusinessSendRecovery actor={userId} query={card?.id} action="share-quote" onRecovered={() => { onStalePricing?.(); onClose(); }} />}
      {!readOnly && pricingReview.below_recommended_gp && <Alert type="warning" message={directPricingAllowed(can, pricingReview) ? "Some parts are below recommended GP. Your preset permits direct sharing, subject to server validation." : "Some parts are below recommended GP and will require approval."} />}
      {unquotedParts.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ fontSize: 11, flexShrink: 0 }}
          message={`${unquotedParts.length} part${unquotedParts.length > 1 ? "s have" : " has"} no selected quote — excluded`}
        />
      )}

      {/* Optional customer delivery; independent from the policy allocation. */}
      <div style={{
        border: `1px solid ${token.colorInfoBorder}`, borderRadius: 6, padding: "8px 10px",
        flexShrink: 0, background: token.colorInfoBg,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <span style={{ fontWeight: 600, fontSize: 12 }}>Delivery charge</span>
          <Checkbox checked={deliveryEnabled} disabled={readOnly} onChange={e => setDeliveryEnabled(e.target.checked)}>Include customer delivery</Checkbox>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <InputNumber
            aria-label="Customer delivery amount"
            value={deliveryAmount}
            min={minDeliveryAmount}
            size="small"
            prefix="AED"
            disabled={readOnly}
            style={{ width: 110 }}
            onChange={v => setDeliveryAmount(Math.max(minDeliveryAmount, v ?? 0))}
          />
          {selectedPartsForInjection.length > 1 && (
            <span style={{ fontSize: 11, color: t.textSub }}>
              ÷ {selectedPartsForInjection.length} parts
              {" = "}
              <strong>AED {(deliveryAmount / selectedPartsForInjection.length).toFixed(2)}/part</strong>
            </span>
          )}
        </div>
      </div>

      {/* One global VAT mode/% applied to every included part in this
          send — replaces the old per-part VAT dropdown/%. */}
      <div style={{
        border: `1px solid ${t.border}`, borderRadius: 6, padding: "8px 10px",
        flexShrink: 0, background: t.headerBg,
      }}>
        <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6 }}>VAT (applies to every included part)</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 168, flexShrink: 0 }}>
            <MiscSelect
              aria-label="VAT mode"
              value={vatMode}
              disabled={readOnly}
              onChange={v => !readOnly && setVatMode(v)}
              size="small"
              style={{ fontSize: 11 }}
              mode="vat_mode"
            />
          </div>
          <InputNumber
            aria-label="VAT %"
            value={vatPct}
            min={0} max={100}
            size="small"
            disabled={readOnly || vatMode === "none"}
            suffix="%"
            style={{ width: 84 }}
            onChange={v => !readOnly && setVatPct(v ?? 0)}
          />
        </div>
      </div>

      <div style={{ fontWeight: 600, fontSize: 12, color: token.colorTextSecondary, flexShrink: 0 }}>Parts to include</div>

      {(readOnly ? (preloadedPayload?.parts ?? []) : parts).map(p => {
        // Normalise field names — live parts use p.id, payload parts use p.query_part_id
        const partKey = readOnly ? p.query_part_id : p.id;
        const quote = readOnly ? null : (p.supplier_quotes ?? []).find(q => q.id === p.selected_quote_id);
        const hasQuote = readOnly ? true : !!quote;
        const checked = readOnly ? !!p.included : includedPartIds.has(partKey);
        const sp = readOnly ? (p.sp ?? 0) : (quote ? (quotePricing[quote.id]?.sp ?? Number(quote.quoted_price || 0)) : null);
        const cp = readOnly ? (p.cp ?? 0) : (pricedById.get(Number(partKey))?.cp ?? Number(quote?.quoted_price || 0));
        const { pct, mode } = readOnly
          ? { pct: p.vat_pct ?? 5, mode: p.vat_mode ?? (p.vat_enabled ? "incl" : "none") }
          : { pct: vatPct, mode: vatMode };
        const warrantyDays = readOnly ? (p.warranty_days ?? 0) : Number(quote?.warranty_days || 0);
        const condition = readOnly
          ? (conditionLabel(p.condition) || "New")
          : (conditionLabel(p.condition_preference ?? p.condition ?? p.part_condition) || "New");

        const rawSp = readOnly ? (p.base_sp ?? sp) : (pricedById.get(Number(partKey))?.base_sp ?? partSp[partKey] ?? sp);
        const currentSp = rawSp ?? 0;
        const gp = hasQuote ? gpFromSp(cp, currentSp) : 0;
        const gpColor = gp > 0 ? token.colorSuccess : token.colorError;
        const withDelivery = rawSp != null ? (readOnly ? sp : pricedById.get(Number(partKey))?.sp ?? rawSp) + (checked ? deliveryPerPart : 0) : null;
        const finalPrice = readOnly ? p.final_price : (withDelivery != null ? money(mode === "incl" ? withDelivery * (1 + pct / 100) : withDelivery) : null);
        const vatSuffix = mode === "incl" ? `(Incl. VAT ${pct}%)` : mode === "excl" ? `+ VAT ${pct}%` : "";
        const showFinal = hasQuote && sp != null;
        const inclVatPrice = withDelivery != null ? money(withDelivery * (1 + pct / 100)) : null;

        return (
          <div
            key={partKey}
            style={{
              border: `1px solid ${checked && hasQuote ? token.colorSuccessBorder : hasQuote ? token.colorBorder : token.colorWarningBorder}`,
              borderRadius: 6,
              padding: "8px 10px",
              opacity: hasQuote ? 1 : 0.55,
              background: checked && hasQuote ? token.colorSuccessBg : undefined,
              flexShrink: 0,
              transition: "background 0.15s, border-color 0.15s",
            }}
          >
            <div style={{ display: "flex", alignItems: "stretch", gap: 10 }}>
              <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                <div
                  style={{ display: "flex", alignItems: "center", gap: 8, cursor: hasQuote && !readOnly ? "pointer" : "default" }}
                  onClick={() => hasQuote && !readOnly && togglePart(partKey)}
                >
                  <Checkbox checked={checked} disabled={!hasQuote || readOnly} onChange={() => {}} />
                  <span style={{ fontWeight: 600, fontSize: 12 }}>{p.part_name}</span>
                  {!hasQuote && <Tag color="orange" style={{ fontSize: 10, margin: 0 }}>No quote</Tag>}
                  {hasQuote && (
                    <Tooltip title={warrantyDays > 0 ? `${warrantyDays} days replacement warranty` : "No warranty"}>
                      <span style={{ color: token.colorTextTertiary, fontSize: 11, cursor: "default" }}>
                        {condition}
                        {warrantyDays > 0 ? ` · ${warrantyDays}d` : " · No warranty"}
                      </span>
                    </Tooltip>
                  )}
                  {hasQuote && (
                    <span onClick={e => e.stopPropagation()}>
                      <Checkbox
                        aria-label={`Use old exchange for ${p.part_name}`}
                        checked={readOnly ? !!p.old_exchange : !!partOldExchange[partKey]}
                        disabled={readOnly}
                        onChange={() => !readOnly && toggleOldExchange(partKey)}
                      >
                        <span style={{ fontSize: 11 }}>With Old Exchange</span>
                      </Checkbox>
                    </span>
                  )}
                </div>
                {hasQuote && (
                  <div style={{ marginTop: 6, marginLeft: 24, fontSize: 11, color: token.colorTextSecondary, lineHeight: 1.7, display: "flex", flexDirection: "column", gap: 4 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }} onClick={e => e.stopPropagation()}>
                      <Tooltip title="Cost price — what the supplier quoted us">
                        <span style={{ cursor: "default" }}>
                          CP: <strong style={{ color: t.text }}>AED {cp.toLocaleString()}</strong>
                        </span>
                      </Tooltip>
                      {" · "}
                      <Tooltip title="GP percentage on supplier cost">
                        <span style={{ cursor: "default" }}>
                          GP: <strong style={{ color: gpColor }}>{gp.toFixed(1)}%</strong>
                        </span>
                      </Tooltip>
                      <span>Base</span>
                      <InputNumber
                        precision={2}
                        aria-label={`Base selling price for ${p.part_name}`}
                        value={rawSp}
                        min={0}
                        size="small"
                        prefix="AED"
                        disabled={readOnly}
                        style={{ width: 110, fontWeight: 600 }}
                        onChange={v => !readOnly && setPartSp(prev => ({ ...prev, [partKey]: v ?? 0 }))}
                      />
                      {checked && deliveryPerPart > 0 && (
                        <Tooltip title={`Delivery share added to this part (${deliveryAmount} ÷ ${selectedPartsForInjection.length} part${selectedPartsForInjection.length > 1 ? "s" : ""})`}>
                          <span style={{ color: token.colorInfo, cursor: "default" }}>
                            +{deliveryPerPart % 1 === 0 ? deliveryPerPart : deliveryPerPart.toFixed(2)} del
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {/* Spans all 3 rows to the left (name row + both detail rows),
                  vertically centered — this is the number that matters most
                  in this block. */}
              {showFinal && (
                <>
                  <div style={{ width: 1, alignSelf: "stretch", background: t.border, flexShrink: 0 }} />
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, flexShrink: 0, padding: "0 2px" }}>
                    <Tooltip title="Final customer-facing price after SP + delivery + VAT">
                      <span style={{
                        color: token.colorSuccess, fontWeight: 700, fontSize: 20,
                        background: token.colorSuccessBg, border: `1px solid ${token.colorSuccessBorder}`,
                        borderRadius: 6, padding: "4px 12px", cursor: "default",
                        whiteSpace: "nowrap",
                      }}>AED {finalPrice?.toLocaleString()}</span>
                    </Tooltip>
                    {vatSuffix && <span style={{ fontSize: 11, color: token.colorTextTertiary }}>{vatSuffix}</span>}
                    {mode !== "none" && inclVatPrice != null && (
                      <span style={{ fontSize: 10, color: token.colorTextTertiary }}>Incl. VAT: AED {inclVatPrice.toLocaleString()}</span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  const rightCol = (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, minHeight: 0 }}>
      {!typeKnown && (
        <Alert
          type="info"
          showIcon
          style={{ fontSize: 11, flexShrink: 0 }}
          message="Customer type not set — select the appropriate template"
        />
      )}
      {!readOnly && (
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size="small"
          style={{ flexShrink: 0 }}
          items={[
            { key: "b2c", label: "B2C · Retail" },
            { key: "b2b", label: "B2B · Garage" },
          ]}
        />
      )}
      {!readOnly && qrLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 32 }}><Spin /></div>
      ) : !readOnly && !actionSnippet ? (
        <Alert type="warning" showIcon message={bindingError || "Loading default message…"} />
      ) : (
        <>
          {!readOnly && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              <Checkbox
                checked={textEditEnabled}
                onChange={e => {
                  setTextEditEnabled(e.target.checked);
                }}
              >
                <span style={{ fontSize: 12 }}>Edit message directly</span>
              </Checkbox>
              {textManuallyEdited && (
                <Tag color="orange" style={{ fontSize: 11, margin: 0 }}>
                  Customised — sent as-is, no re-injection
                </Tag>
              )}
              {textManuallyEdited && textEditEnabled && (
                <Tooltip title="Discard your edits and go back to auto-generated text">
                  <Button
                    size="small"
                    type="text"
                    style={{ fontSize: 11, color: token.colorTextTertiary, padding: "0 4px" }}
                    onClick={() => {
                      setTextManuallyEdited(false);
                      setTextEditEnabled(false);
                      const qr = actionSnippet;
                      if (qr) {
                        const base = substituteQRText(qr.body || "", authState);
                        setMessageText(injectPartsTemplate(base, card, selectedPartsForInjection, deliveryPerPart));
                      }
                    }}
                  >
                    Reset to template
                  </Button>
                </Tooltip>
              )}
            </div>
          )}
          <Input.TextArea
            value={messageText}
            readOnly={readOnly || !textEditEnabled}
            onChange={e => {
              if (readOnly) return;
              setMessageText(e.target.value);
              setTextManuallyEdited(true);
            }}
            style={{
              fontSize: 12, fontFamily: "inherit", flex: 1, resize: "none", overflowY: "auto",
              background: !readOnly && textEditEnabled ? t.input : t.headerBg,
              cursor: !readOnly && textEditEnabled ? "text" : "default",
              color: !readOnly && textEditEnabled ? t.text : t.textSub,
            }}
            placeholder="Message preview…"
          />
        </>
      )}
    </div>
  );

  return (
    <>
    <Modal
      open={open}
      onCancel={onClose}
      title={readOnly
        ? <span><EyeOutlined style={{ marginRight: 6 }} />Quote preview — pending approval</span>
        : <span><ShareAltOutlined style={{ marginRight: 6 }} />Share quote to customer</span>
      }
      style={{ top: 16, paddingBottom: 0 }}
      styles={{
        content: { height: "calc(100vh - 32px)", display: "flex", flexDirection: "column", padding: 0, overflow: "hidden" },
        header: { padding: "16px 24px", flexShrink: 0 },
        body: { flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column", padding: "0 24px" },
        footer: { padding: "12px 24px", flexShrink: 0 },
      }}
      footer={
        readOnly ? (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={onClose}>Close</Button>
          </div>
        ) : (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              aria-label="Send quote to customer"
              type="primary"
              icon={<SendOutlined />}
              loading={sending}
              disabled={!messageText.trim() || qrLoading || !actionSnippet || !!bindingError || !!revision.error || !revision.allowed || (revision.locked && !revision.reason)}
              onClick={handleSend}
            >
              {revision.locked ? 'Share revised quote' : 'Send to customer'}
            </Button>
          </div>
        )
      }
      width={940}
      destroyOnHidden
      zIndex={1050}
    >
      <div style={{ display: "grid", gridTemplateColumns: "520px 1fr", gap: 16, flex: 1, minHeight: 0 }}>
        {leftCol}
        {rightCol}
      </div>
    </Modal>
    <Modal
      open={!!manualShare}
      title="Confirm manual quote sharing"
      okText="Record quote as shared"
      cancelText="Cancel"
      confirmLoading={recordingManual}
      onOk={handleManualShare}
      onCancel={() => setManualShare(null)}
      destroyOnHidden
      zIndex={1060}
    >
      <Alert
        type="warning"
        showIcon
        message={`Customer communication: ${manualShare?.channel?.label || "Manual channel"}`}
        description={`This customer/query uses ${manualShare?.channel?.label || "a manual channel"} as the preferred communication channel. Continuing will record that the quote was shared manually; Uzdpart360 will not send an automated WATI message.`}
      />
    </Modal>
    </>
  );
}
